import React, { PureComponent } from 'react';
import { Box, Flex } from 'rebass';
import styled from 'styled-components';
import { connect } from 'unistore/react';

import Actions from '~/state/Actions';
import { weightedIncomeSelector } from '~/state/Selectors';
import { nf, trackEvent } from '~/Utils';
import InfoIcon from '~/components/Header/Assets/info.svg';

import NumericInput from './NumericInput';

const ListItem = styled(Flex).attrs({
  justifyContent: 'space-between',
  py: 2,
})`
  border-top: ${(props) => (props.borderTop ? `1px solid ${props.theme.colors.lightblue}` : 'none')};
`;

const Label = styled.div`
  font-size: 14px;
  width: 60%;
`;

const InputWrapper = styled.div`
  width: 40%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const InfoIconStyled = styled(InfoIcon)`
  width: 15px;
  height: 15px;
  margin-left: 5px;
`;

const InfoBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 220px;
  pointer-events: none;
  user-select: none;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.9);
  padding: 5px;
  border: 1px solid ${(p) => p.theme.colors.blue};
  z-index: 100;
`;

const IncomeDisplay = styled.div`
  text-align: center;
  width: 100%;
  font-weight: bold;
  max-width: 180px;
`;

class IncomeCalculator extends PureComponent {
  state = {
    showInfo: false,
    infoPosition: false,
  };

  infoButtonRef = React.createRef();

  containerRef = React.createRef();

  handleInfoIcon() {
    const infoBtnPosition = this.infoButtonRef.current.getBoundingClientRect();
    const containerPosition = this.containerRef.current.getBoundingClientRect();

    const infoPosition = {
      top: infoBtnPosition.top - containerPosition.top + 20,
      left: Math.max(infoBtnPosition.left - containerPosition.left - 100, 0),
    };

    this.setState({
      showInfo: true,
      infoPosition,
    });
  }

  handleInfoIconOut() {
    this.setState({ showInfo: false });
  }

  render() {
    const { weightedIncome, householdIncome, householdMembersAbove14, householdMembersBelow14 } = this.props;

    const { infoPosition, showInfo } = this.state;

    const infoStyle = infoPosition
      ? {
          top: infoPosition.top,
          left: infoPosition.left,
        }
      : {};

    const onIncomeChange = (income) => {
      this.props.setHouseholdIncome(income);
      trackEvent('set_income', income);
    };

    const setAbove14 = (above14) => {
      this.props.setHouseholdMembersAbove14(above14);
      trackEvent('set_above14', above14);
    };

    const setBelow14 = (below14) => {
      this.props.setHouseholdMembersBelow14(below14);
      trackEvent('set_below14', below14);
    };

    const parser = (value) => {
      const result = +value.replace('.', '');
      if (isNaN(result)) {
        return 0;
      }
      return Math.max(0, Math.min(result, 100000));
    };

    return (
      <Box style={{ position: 'relative' }} my={3} ref={this.containerRef}>
        {showInfo && (
          <InfoBox style={infoStyle}>
            Einkommen umfassen alle Einnahmen der Haushaltsmitglieder, zum Beispiel Löhne, Zinsen, Mieteinnahmen,
            Unterhaltszahlungen, Renten (nach Abzug von Steuern und inklusive Transfers wie Kindergeld). Im Fall einer
            selbst bewohnten Immobilie werden auch die gesparten Mietausgaben addiert (abzüglich der Kosten für
            Hypotheken und Instandhaltung).
          </InfoBox>
        )}
        <ListItem>
          <Label>
            Wie hoch ist Ihr monatliches Haushaltsnettoeinkommen in Euro?
            <span ref={this.infoButtonRef}>
              <InfoIconStyled
                onMouseEnter={() => this.handleInfoIcon()}
                onMouseLeave={() => this.handleInfoIconOut()}
              />
            </span>
          </Label>
          <InputWrapper>
            <NumericInput
              formatter={nf}
              parser={parser}
              min={0}
              step={100}
              max={100000}
              onChange={onIncomeChange}
              value={householdIncome}
            />
          </InputWrapper>
        </ListItem>
        <ListItem>
          <Label>
            Wie viele Personen <strong>ab</strong> 14 Jahren leben in Ihrem Haushalt?
          </Label>
          <InputWrapper>
            <NumericInput min={1} max={10} onChange={setAbove14} value={householdMembersAbove14} />
          </InputWrapper>
        </ListItem>
        <ListItem>
          <Label>
            Wie viele Personen <strong>unter</strong> 14 Jahren leben in Ihrem Haushalt?
          </Label>
          <InputWrapper>
            <NumericInput min={0} max={10} onChange={setBelow14} value={householdMembersBelow14} />
          </InputWrapper>
        </ListItem>
        <ListItem borderTop>
          <Label>
            Jedes Haushaltsmitglied erreicht den Lebensstandard eines Singles mit einem monatlichen Nettoeinkommen von{' '}
          </Label>
          <InputWrapper>
            <IncomeDisplay>{nf(weightedIncome)} Euro</IncomeDisplay>
          </InputWrapper>
        </ListItem>
      </Box>
    );
  }
}

export default connect(
  (state) => ({
    weightedIncome: weightedIncomeSelector(state),
    householdIncome: state.householdIncome,
    householdMembersAbove14: state.householdMembersAbove14,
    householdMembersBelow14: state.householdMembersBelow14,
  }),
  Actions
)(IncomeCalculator);
